package it.maicol07.gamerlogue.`data`

import it.maicol07.spraypaintkt.CollectionProxy
import it.maicol07.spraypaintkt.RecordProxy
import it.maicol07.spraypaintkt.Resource
import it.maicol07.spraypaintkt.ResourceRegistry
import it.maicol07.spraypaintkt.ResourceSerializer
import it.maicol07.spraypaintkt.Scope
import it.maicol07.spraypaintkt.SortDirection
import it.maicol07.spraypaintkt.extensions.DirtyMap
import it.maicol07.spraypaintkt.extensions.trackChanges
import it.maicol07.spraypaintkt.interfaces.JsonApiConfig
import it.maicol07.spraypaintkt.registerResources
import kotlin.Any
import kotlin.Boolean
import kotlin.Int
import kotlin.Number
import kotlin.String
import kotlin.Unit
import kotlin.collections.List
import kotlin.collections.MutableMap
import kotlin.reflect.KClass
import kotlinx.serialization.Serializable

@Serializable(User.Companion.Serializer::class)
public class User : Resource, UserSchema {
  override val `companion`: Resource.CompanionObj<User> = Companion

  override var id: String? = null

  override var isPersisted: Boolean = false

  override val attributes: DirtyMap<String, Any?> = mutableMapOf<String, Any?>().trackChanges()

  override val relationships: DirtyMap<String, Any?> = mutableMapOf<String, Any?>().trackChanges()

  override val meta: MutableMap<String, Any?> = mutableMapOf()

  override val links: MutableMap<String, Any?> = mutableMapOf()

  override val type: String by lazy { companion.resourceType }

  override var nickname: String?
    get() = if (attributes.containsKey("nickname")) {
      attributes["nickname"] as String?
    } else {
      null
    }
    set(`value`) {
      attributes["nickname"] = value
    }

  override var name: String?
    get() = if (attributes.containsKey("name")) {
      attributes["name"] as String?
    } else {
      null
    }
    set(`value`) {
      attributes["name"] = value
    }

  override var firstName: String?
    get() = if (attributes.containsKey("first_name")) {
      attributes["first_name"] as String?
    } else {
      null
    }
    set(`value`) {
      attributes["first_name"] = value
    }

  override var lastName: String?
    get() = if (attributes.containsKey("last_name")) {
      attributes["last_name"] as String?
    } else {
      null
    }
    set(`value`) {
      attributes["last_name"] = value
    }

  override var picture: String?
    get() = if (attributes.containsKey("picture")) {
      attributes["picture"] as String?
    } else {
      null
    }
    set(`value`) {
      attributes["picture"] = value
    }

  override var email: String?
    get() = if (attributes.containsKey("email")) {
      attributes["email"] as String?
    } else {
      null
    }
    set(`value`) {
      attributes["email"] = value
    }

  override var emailVerifiedAt: String?
    get() = if (attributes.containsKey("email_verified_at")) {
      attributes["email_verified_at"] as String?
    } else {
      null
    }
    set(`value`) {
      attributes["email_verified_at"] = value
    }

  override var bio: String?
    get() = if (attributes.containsKey("bio")) {
      attributes["bio"] as String?
    } else {
      null
    }
    set(`value`) {
      attributes["bio"] = value
    }

  override var bannerGameId: Int?
    get() = if (attributes.containsKey("banner_game_id")) {
      attributes["banner_game_id"] as Int?
    } else {
      null
    }
    set(`value`) {
      attributes["banner_game_id"] = value
    }

  override var bannerImageId: String?
    get() = if (attributes.containsKey("banner_image_id")) {
      attributes["banner_image_id"] as String?
    } else {
      null
    }
    set(`value`) {
      attributes["banner_image_id"] = value
    }

  override var platformsIds: List<Int>
    get() = if (attributes.containsKey("platforms_ids")) {
      attributes["platforms_ids"] as List<Int>
    } else {
      super.platformsIds
    }
    set(`value`) {
      attributes["platforms_ids"] = value
    }

  override var visibility: String
    get() = if (attributes.containsKey("visibility")) {
      attributes["visibility"] as String
    } else {
      super.visibility
    }
    set(`value`) {
      attributes["visibility"] = value
    }

  override var pictureEditUrl: String?
    get() = if (attributes.containsKey("picture_edit_url")) {
      attributes["picture_edit_url"] as String?
    } else {
      null
    }
    set(`value`) {
      attributes["picture_edit_url"] = value
    }

  override var followersCount: Int
    get() = if (attributes.containsKey("followers_count")) {
      attributes["followers_count"] as Int
    } else {
      super.followersCount
    }
    set(`value`) {
      attributes["followers_count"] = value
    }

  override var followingCount: Int
    get() = if (attributes.containsKey("following_count")) {
      attributes["following_count"] as Int
    } else {
      super.followingCount
    }
    set(`value`) {
      attributes["following_count"] = value
    }

  override var isFollowedByMe: Boolean
    get() = if (attributes.containsKey("is_followed_by_me")) {
      attributes["is_followed_by_me"] as Boolean
    } else {
      super.isFollowedByMe
    }
    set(`value`) {
      attributes["is_followed_by_me"] = value
    }

  override var followsMe: Boolean
    get() = if (attributes.containsKey("follows_me")) {
      attributes["follows_me"] as Boolean
    } else {
      super.followsMe
    }
    set(`value`) {
      attributes["follows_me"] = value
    }

  override var createdAt: String?
    get() = if (attributes.containsKey("created_at")) {
      attributes["created_at"] as String?
    } else {
      null
    }
    set(`value`) {
      attributes["created_at"] = value
    }

  override var updatedAt: String?
    get() = if (attributes.containsKey("updated_at")) {
      attributes["updated_at"] as String?
    } else {
      null
    }
    set(`value`) {
      attributes["updated_at"] = value
    }

  public constructor(
    nickname: String? = null,
    name: String? = null,
    firstName: String? = null,
    lastName: String? = null,
    picture: String? = null,
    email: String? = null,
    emailVerifiedAt: String? = null,
    bio: String? = null,
    bannerGameId: Int? = null,
    bannerImageId: String? = null,
    platformsIds: List<Int>? = null,
    visibility: String? = null,
    pictureEditUrl: String? = null,
    followersCount: Int? = null,
    followingCount: Int? = null,
    isFollowedByMe: Boolean? = null,
    followsMe: Boolean? = null,
    createdAt: String? = null,
    updatedAt: String? = null,
  ) {
    if (nickname != null) {
      attributes["nickname"] = nickname
    }
    if (name != null) {
      attributes["name"] = name
    }
    if (firstName != null) {
      attributes["first_name"] = firstName
    }
    if (lastName != null) {
      attributes["last_name"] = lastName
    }
    if (picture != null) {
      attributes["picture"] = picture
    }
    if (email != null) {
      attributes["email"] = email
    }
    if (emailVerifiedAt != null) {
      attributes["email_verified_at"] = emailVerifiedAt
    }
    if (bio != null) {
      attributes["bio"] = bio
    }
    if (bannerGameId != null) {
      attributes["banner_game_id"] = bannerGameId
    }
    if (bannerImageId != null) {
      attributes["banner_image_id"] = bannerImageId
    }
    if (platformsIds != null) {
      attributes["platforms_ids"] = platformsIds
    }
    if (visibility != null) {
      attributes["visibility"] = visibility
    }
    if (pictureEditUrl != null) {
      attributes["picture_edit_url"] = pictureEditUrl
    }
    if (followersCount != null) {
      attributes["followers_count"] = followersCount
    }
    if (followingCount != null) {
      attributes["following_count"] = followingCount
    }
    if (isFollowedByMe != null) {
      attributes["is_followed_by_me"] = isFollowedByMe
    }
    if (followsMe != null) {
      attributes["follows_me"] = followsMe
    }
    if (createdAt != null) {
      attributes["created_at"] = createdAt
    }
    if (updatedAt != null) {
      attributes["updated_at"] = updatedAt
    }
  }

  public companion object : Resource.CompanionObj<User> {
    override val resourceType: String = "User"

    override val endpoint: String = "users"

    override val config: JsonApiConfig = AppJsonApiConfig

    override val factory: () -> User = { User() }

    override val schema: KClass<UserSchema> = UserSchema::class

    init {
      ResourceRegistry.registerResources()
    }

    public fun scope(options: Scope<User>.() -> Unit = {}): Scope<User> = Scope(User::class, options)

    public final suspend fun all(): CollectionProxy<User> = scope().all()

    public final suspend fun find(id: String): RecordProxy<User> = scope().find(id)

    public final suspend fun findOrNull(id: String): RecordProxy<User?> = scope().findOrNull(id)

    public final suspend fun first(): RecordProxy<User> = scope().first()

    public final suspend fun firstOrNull(): RecordProxy<User?> = scope().firstOrNull()

    public final suspend fun exists(): Boolean = scope().exists()

    public final suspend fun last(): RecordProxy<User> = scope().last()

    public final suspend fun lastOrNull(): RecordProxy<User?> = scope().lastOrNull()

    public final fun page(pageNumber: Number): Scope<User> = scope().page(pageNumber)

    public final fun per(pageSize: Number): Scope<User> = scope().per(pageSize)

    public final fun limit(limit: Number): Scope<User> = scope().limit(limit)

    public final fun offset(offset: Number): Scope<User> = scope().offset(offset)

    public final fun `where`(attribute: String, `value`: Any): Scope<User> = scope().where(attribute, value)

    public final fun extraParam(key: String, `value`: String): Scope<User> = scope().extraParam(key, value)

    public final fun order(attribute: String): Scope<User> = scope().order(attribute)

    public final fun order(attribute: String, sortDirection: SortDirection): Scope<User> = scope().order(attribute, sortDirection)

    public final fun includes(vararg relationships: String): Scope<User> = scope().includes(*relationships)

    public final fun select(type: String, vararg fields: String): Scope<User> = scope().select(type, *fields)

    public object Serializer : ResourceSerializer<User>()
  }
}
